import {
  Avatar,
  Box,
  Button,
  Chip,
  CircularProgress,
  Container,
  Divider,
  Grid,
  Paper,
  Rating,
  Stack,
  Typography,
} from "@mui/material";
import LocationOnIcon from "@mui/icons-material/LocationOn";
import WorkHistoryIcon from "@mui/icons-material/WorkHistory";
import PaymentsIcon from "@mui/icons-material/Payments";
import VerifiedIcon from "@mui/icons-material/VerifiedUser";
import { useQuery } from "@tanstack/react-query";
import { useNavigate, useParams } from "react-router-dom";
import { getDoctor, listDoctorReviews } from "@/api/userApi";
import { useAuth } from "@/auth/AuthContext";

export default function DoctorDetailPage() {
  const { id = "" } = useParams();
  const navigate = useNavigate();
  const { isAuthenticated, isPatient, login } = useAuth();

  const { data: doc, isLoading } = useQuery({
    queryKey: ["doctor", id],
    queryFn: () => getDoctor(id),
    enabled: Boolean(id),
  });

  const { data: reviews } = useQuery({
    queryKey: ["doctor-reviews", id],
    queryFn: () => listDoctorReviews(id),
    enabled: Boolean(id),
  });
  const reviewList = Array.isArray(reviews) ? reviews : [];

  if (isLoading || !doc) {
    return (
      <Box sx={{ display: "grid", placeItems: "center", minHeight: "60vh" }}>
        <CircularProgress />
      </Box>
    );
  }

  const handleBook = () => {
    if (!isAuthenticated) {
      login(`/patient/book/${doc.keycloakId}`);
      return;
    }
    if (!isPatient) {
      navigate("/unauthorized");
      return;
    }
    navigate(`/patient/book/${doc.keycloakId}`);
  };

  return (
    <Container maxWidth="lg" sx={{ py: { xs: 4, md: 6 } }}>
      <Paper variant="outlined" sx={{ p: { xs: 3, md: 4 } }}>
        <Grid container spacing={4} alignItems="center">
          <Grid item>
            <Avatar
              src={doc.photoUrl}
              sx={{
                width: 120,
                height: 120,
                bgcolor: "primary.main",
                fontSize: 48,
                fontWeight: 800,
              }}
            >
              {doc.fullName?.[0]}
            </Avatar>
          </Grid>
          <Grid item xs>
            <Stack direction="row" spacing={1} alignItems="center" mb={0.5}>
              <Typography variant="h4">{doc.fullName}</Typography>
              <Chip
                icon={<VerifiedIcon />}
                color="primary"
                size="small"
                label="Verified"
              />
            </Stack>
            <Typography color="text.secondary" gutterBottom>
              {doc.specialization} {doc.qualification && `· ${doc.qualification}`}
            </Typography>
            <Stack direction="row" spacing={1} alignItems="center">
              <Rating
                value={doc.averageRating ?? 0}
                precision={0.1}
                readOnly
                size="small"
              />
              <Typography variant="body2" color="text.secondary">
                {(doc.averageRating ?? 0).toFixed(1)} · {doc.reviewCount ?? 0}{" "}
                reviews
              </Typography>
            </Stack>
          </Grid>
          <Grid item>
            <Stack spacing={1} alignItems="flex-end">
              {doc.consultationFee != null && (
                <Chip
                  icon={<PaymentsIcon />}
                  color="warning"
                  label={`₹${doc.consultationFee} / consult`}
                  sx={{ fontWeight: 700 }}
                />
              )}
              <Button variant="contained" size="large" onClick={handleBook}>
                Book appointment
              </Button>
            </Stack>
          </Grid>
        </Grid>

        <Divider sx={{ my: 3 }} />

        <Grid container spacing={4}>
          <Grid item xs={12} md={8}>
            <Typography variant="h6" gutterBottom>
              About
            </Typography>
            <Typography color="text.secondary" sx={{ mb: 3 }}>
              {doc.bio ||
                "This doctor hasn't added a bio yet. Their verification and credentials have been confirmed by ChikitsaLink."}
            </Typography>

            <Typography variant="h6" gutterBottom>
              Reviews
            </Typography>
            {reviewList.length === 0 ? (
              <Typography color="text.secondary">
                No reviews yet. Be the first to review after your consultation.
              </Typography>
            ) : (
              <Stack spacing={2}>
                {reviewList.map((r) => (
                  <Paper key={r.id} variant="outlined" sx={{ p: 2 }}>
                    <Stack
                      direction="row"
                      justifyContent="space-between"
                      alignItems="center"
                    >
                      <Typography fontWeight={700}>
                        {r.patientName ?? "Patient"}
                      </Typography>
                      <Rating size="small" value={r.rating} readOnly />
                    </Stack>
                    {r.comment && (
                      <Typography sx={{ mt: 1 }} color="text.secondary">
                        {r.comment}
                      </Typography>
                    )}
                  </Paper>
                ))}
              </Stack>
            )}
          </Grid>

          <Grid item xs={12} md={4}>
            <Paper variant="outlined" sx={{ p: 2.5 }}>
              <Stack spacing={1.5}>
                {doc.experienceYears != null && (
                  <InfoRow
                    icon={<WorkHistoryIcon color="primary" />}
                    label="Experience"
                    value={`${doc.experienceYears} years`}
                  />
                )}
                {doc.city && (
                  <InfoRow
                    icon={<LocationOnIcon color="primary" />}
                    label="City"
                    value={doc.city}
                  />
                )}
                {doc.registrationNumber && (
                  <InfoRow
                    icon={<VerifiedIcon color="primary" />}
                    label="Reg no."
                    value={doc.registrationNumber}
                  />
                )}
                {doc.languages && doc.languages.length > 0 && (
                  <Box>
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      sx={{ display: "block", mb: 0.5 }}
                    >
                      Languages
                    </Typography>
                    <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap>
                      {doc.languages.map((l) => (
                        <Chip key={l} label={l} size="small" variant="outlined" />
                      ))}
                    </Stack>
                  </Box>
                )}
              </Stack>
            </Paper>
          </Grid>
        </Grid>
      </Paper>
    </Container>
  );
}

function InfoRow({
  icon,
  label,
  value,
}: {
  icon: React.ReactNode;
  label: string;
  value: string;
}) {
  return (
    <Stack direction="row" spacing={1.5} alignItems="center">
      {icon}
      <Box>
        <Typography variant="caption" color="text.secondary">
          {label}
        </Typography>
        <Typography fontWeight={600}>{value}</Typography>
      </Box>
    </Stack>
  );
}
